import * as React from 'react';
import { graphql } from 'react-apollo';

import classNames from 'classnames';
import omit from 'lodash/omit';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';

import { buildHashWithAuthMode, getLocationHash } from 'js/lib/authModeDeepLinkUtils';
import user from 'js/lib/user';

import withSessionsV2EnrollmentEnabled from 'bundles/course-sessions/utils/withSessionsV2EnrollmentEnabled';
import StartDateString from 'bundles/enroll-common/components/StartDateString';
import { getFormattedCourseDataFromQuery } from 'bundles/enroll-common/components/xdp/enrollSectionUtils';
import withEnrollment from 'bundles/enroll-common/components/xdp/withEnrollment';
import type { EnrollmentChoices } from 'bundles/enroll-common/components/xdp/withEnrollment';
import GetCourseForXdpEnrollCta from 'bundles/enroll-common/queries/GetCourseForXdpEnrollCtaQuery.graphql';
import type {
  GetCourseForXdpEnrollCtaQuery,
  GetCourseForXdpEnrollCtaQueryVariables,
} from 'bundles/enroll-common/queries/__generated__/GetCourseForXdpEnrollCtaQuery';
import type { FormattedCourse } from 'bundles/enroll-common/types/enrollSectionTypes';
import { ENROLL } from 'bundles/enroll-common/utils/enrollActionParams';
import EnrollModal from 'bundles/enroll-modal/components/EnrollModal';
import Icon from 'bundles/iconfont/Icon';
import TrackedButton from 'bundles/page/components/TrackedButton';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import uriManager from 'bundles/page/lib/uriManager';
import TopLevelModal from 'bundles/phoenix/components/TopLevelModal';

import _t from 'i18n!nls/enroll-course';

import 'css!./__styles__/CourseEnrollButton';

export type EnrollButtonRenderProps = {
  disabled: boolean;
  enrollButtonText: string;
  onClick?: (ev: React.MouseEvent<HTMLButtonElement>) => void;
  href?: string;
  /** Descriptive aria-label for screen readers (e.g. "Enroll in Course Name course") */
  ariaLabel?: string;
};

type PropsFromCaller = {
  courseId: string;
  extraClassName?: string;
  showStartDate?: boolean;
  enrollButtonText?: string;
  renderEnrollButton?: (props: EnrollButtonRenderProps) => React.ReactNode;
};

type PropsFromGraphql = {
  course?: FormattedCourse;
};

type PropsToComponent = PropsFromCaller &
  PropsFromGraphql & {
    enrollmentAvailableChoices?: EnrollmentChoices;
    isDashboard?: boolean; // TODO (Billy) revisit this after experiment
    onClick?: () => void;
    buttonClickTrackingName?: string;
    buttonTextClassName?: string;
    buttonElementNameOverride?: string;
    hideIfEnrolled?: boolean;
    data: Record<string, string | number>;
    s12nId?: string;
    enrollButtonScreenReaderText: React.ReactNode | null;
    sessionsV2EnrollmentEnabled: boolean;
  };

type State = {
  showCourseEnrollModal: boolean;
  didMount: boolean;
};

class CourseEnrollButtonWithData extends React.Component<PropsToComponent, State> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof CourseEnrollButtonWithData.contextTypes>;

  static defaultProps = {
    showStartDate: true,
    buttonClickTrackingName: 'enroll_button',
    buttonTextClassName: 'headline-1-text enroll-text',
  };

  state: State = {
    showCourseEnrollModal: false,
    didMount: false,
  };

  componentDidMount() {
    const action = uriManager.parseAndDeleteQueryParam('action');
    const isAuthenticated = user.isAuthenticatedUser();

    const showCourseEnrollModal = this.isEnrollable() && isAuthenticated && action === ENROLL;
    this.setState(() => ({ didMount: true, showCourseEnrollModal }));
  }

  handleModelClose = (): void => {
    this.setState(() => ({ showCourseEnrollModal: false }));
  };

  onClick(ev: React.MouseEvent<HTMLButtonElement>) {
    ev.preventDefault();
    const { router } = this.context;
    const isAuthenticated = user.isAuthenticatedUser();

    if (isAuthenticated) {
      this.setState(() => ({ showCourseEnrollModal: true }));
    } else {
      router.replace({
        pathname: router.location.pathname,
        query: omit({ ...router.location.query, action: ENROLL }, 'authMode'),
        hash: buildHashWithAuthMode(getLocationHash(router.location), 'signup'),
      });
    }

    if (typeof this.props.onClick === 'function') {
      this.props.onClick();
    }
  }

  isEnrolled() {
    const { enrollmentAvailableChoices } = this.props;
    return enrollmentAvailableChoices && enrollmentAvailableChoices.isEnrolled;
  }

  isCapstoneAccessLocked() {
    const { enrollmentAvailableChoices } = this.props;
    return enrollmentAvailableChoices && enrollmentAvailableChoices.isCapstoneAccessLocked;
  }

  isPreEnrolled() {
    const { course } = this.props;
    return this.isEnrolled() && course?.isPreEnroll;
  }

  isLinkToCourseHome() {
    return this.isEnrolled() && !this.isCapstoneAccessLocked() && !this.isPreEnrolled();
  }

  isEnrollable() {
    return !this.isEnrolled();
  }

  render() {
    const {
      extraClassName,
      courseId,
      course,
      showStartDate,
      isDashboard,
      enrollButtonText = _t('Enroll'),
      buttonClickTrackingName,
      buttonTextClassName,
      buttonElementNameOverride,
      data,
      s12nId,
      hideIfEnrolled,
      enrollButtonScreenReaderText,
      sessionsV2EnrollmentEnabled,
      renderEnrollButton,
    } = this.props;
    const { showCourseEnrollModal, didMount } = this.state;

    const elClassName = buttonElementNameOverride || 'rc-CourseEnrollButton';
    const buttonClassName = classNames('course-enroll-button-wrapper', extraClassName);

    const isEnrolled = this.isEnrolled();
    const isLinkToCourseHome = this.isLinkToCourseHome();
    const isEnrollable = this.isEnrollable();

    const shouldShowStartDate = !isDashboard && (sessionsV2EnrollmentEnabled || showStartDate);
    const startDateNode = course && shouldShowStartDate ? <StartDateString course={course} /> : null;

    const renderLoadingButton = () => (
      <div className={classNames(elClassName, 'is-loading')}>
        <button type="button" className={buttonClassName} disabled={true}>
          <Icon name="spinner" className="cif-spin" />
        </button>
      </div>
    );

    if (course && isEnrolled) {
      // For enrolled courses that launched, display secondary button to dashboard
      // Users with group memberships or private session get link to course home instead
      if (hideIfEnrolled) {
        return null;
      } else if (isLinkToCourseHome) {
        const redirectTrigger = renderEnrollButton ? (
          renderEnrollButton({
            href: course.homeLink,
            disabled: false,
            enrollButtonText: _t('Go to course'),
            ariaLabel: _t('Go to #{courseName} course', { courseName: course.name }),
          })
        ) : (
          <TrackedA
            href={course.homeLink}
            className={classNames(buttonClassName, 'link-button secondary')}
            trackingName="go_to_course_button"
            aria-label={_t('Go to #{courseName} course', { courseName: course.name })}
          >
            <span className={buttonTextClassName}>{_t('Go to course')}</span>
          </TrackedA>
        );
        return <div className={elClassName}>{redirectTrigger}</div>;
      } else {
        // For enrolled courses that are pre-enroll or locked, display disabled button with start date (if relevant)
        const enrolledTrigger = renderEnrollButton ? (
          renderEnrollButton({
            disabled: true,
            enrollButtonText: _t('Enrolled'),
            ariaLabel: _t('Enrolled in #{courseName} course', { courseName: course.name }),
          })
        ) : (
          <button type="button" className={classNames(buttonClassName, 'primary')} disabled={true}>
            <span className={buttonTextClassName}>{_t('Enrolled')}</span>
          </button>
        );
        return <div className={classNames(elClassName, 'is-enrolled')}>{enrolledTrigger}</div>;
      }
    } else if (course && isEnrollable) {
      // Display enroll button with click handler
      const enrollTrigger = renderEnrollButton ? (
        renderEnrollButton({
          onClick: (ev: React.MouseEvent<HTMLButtonElement>) => this.onClick(ev),
          disabled: !didMount,
          enrollButtonText: enrollButtonText ?? _t('Enroll'),
          ariaLabel: _t('Enroll in #{courseName} course', { courseName: course.name }),
        })
      ) : (
        <TrackedButton
          className={classNames(buttonClassName, 'primary')}
          trackingName={buttonClickTrackingName}
          data={{ id: courseId, ...data }}
          onClick={(ev) => this.onClick(ev)}
          disabled={!didMount}
          id="course-enroll-button"
          aria-label={_t('Enroll in #{courseName} course', { courseName: course.name })}
          aria-labelledby="course-enroll-button start-date-string"
          data-e2e="enroll-button"
        >
          {enrollButtonScreenReaderText}
          <span className={buttonTextClassName}>{enrollButtonText}</span>
          {startDateNode}
        </TrackedButton>
      );

      return (
        <div className={elClassName}>
          {enrollTrigger}
          {showCourseEnrollModal && (
            <TopLevelModal>
              <EnrollModal
                courseId={courseId}
                s12nId={s12nId}
                onClose={() => this.setState({ showCourseEnrollModal: false })}
              />
            </TopLevelModal>
          )}
        </div>
      );
    } else {
      return renderLoadingButton();
    }
  }
}

export default compose<PropsToComponent, PropsFromCaller>(
  withSessionsV2EnrollmentEnabled(({ courseId }) => courseId),
  graphql<PropsFromCaller, GetCourseForXdpEnrollCtaQuery, GetCourseForXdpEnrollCtaQueryVariables, PropsFromGraphql>(
    GetCourseForXdpEnrollCta,
    {
      skip: ({ courseId }) => !courseId,
      options: ({ courseId }) => ({ variables: { id: courseId! } }),
      props: ({ data }) => ({ course: getFormattedCourseDataFromQuery(data).course }),
    }
  ),
  withEnrollment()
)(CourseEnrollButtonWithData);
